import { motion } from 'framer-motion';
import { X, FileImage, FileVideo, FileAudio, File as FileIcon, FileText, Loader2, Check, AlertTriangle } from 'lucide-react';
import { formatBytes } from '@/lib/intakeUpload';

/**
 * WorkPreviewCard — a single uploaded work row inside the dropzone.
 * Shows file metadata + optional title/description + upload status.
 */

const TYPE_ICON = {
  image: FileImage,
  video: FileVideo,
  audio: FileAudio,
  pdf: FileText,
  other: FileIcon,
};

function StatusPill({ status, progress }) {
  if (status === 'queued') {
    return <span className="text-[9px] font-mono text-[#888] tracking-[0.18em] uppercase">Ready</span>;
  }
  if (status === 'uploading') {
    return (
      <span className="text-[9px] font-mono text-[#90E0EF] tracking-[0.18em] uppercase flex items-center gap-1.5">
        <Loader2 className="w-3 h-3 animate-spin" />
        {progress}%
      </span>
    );
  }
  if (status === 'done') {
    return (
      <span className="text-[9px] font-mono text-[#7ed4a0] tracking-[0.18em] uppercase flex items-center gap-1.5">
        <Check className="w-3 h-3" /> Uploaded
      </span>
    );
  }
  if (status === 'error') {
    return (
      <span className="text-[9px] font-mono text-red-400 tracking-[0.18em] uppercase flex items-center gap-1.5">
        <AlertTriangle className="w-3 h-3" /> Failed
      </span>
    );
  }
  return null;
}

export default function WorkPreviewCard({ entry, onRemove, onChange, disabled }) {
  const file = entry.file;
  const Icon = TYPE_ICON[entry.fileType] || FileIcon;
  const isImage = entry.fileType === 'image';
  const isVideo = entry.fileType === 'video';
  const objectUrl = entry.objectUrl;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -4 }}
      className="border border-[#222] bg-[#0a0a0a] p-4"
      data-testid={`work-card-${entry.id}`}
    >
      <div className="flex items-start gap-4">
        {/* Thumbnail / icon */}
        <div className="w-16 h-16 sm:w-20 sm:h-20 border border-[#222] bg-[#050505] shrink-0 flex items-center justify-center overflow-hidden">
          {isImage && objectUrl ? (
            <img src={objectUrl} alt={file.name} className="w-full h-full object-cover" />
          ) : isVideo && objectUrl ? (
            <video src={objectUrl} className="w-full h-full object-cover" muted playsInline preload="metadata" />
          ) : (
            <Icon className="w-6 h-6 text-[#666]" />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-xs font-mono text-white truncate">{file.name}</p>
              <p className="text-[10px] font-mono text-[#666] mt-0.5">
                {(entry.fileType || 'file').toUpperCase()} · {formatBytes(file.size)}
                {entry.dimensions ? ` · ${entry.dimensions.width}×${entry.dimensions.height}` : ''}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <StatusPill status={entry.status} progress={entry.progress || 0} />
              {!disabled && entry.status !== 'uploading' && (
                <button
                  type="button"
                  onClick={() => onRemove(entry.id)}
                  data-testid={`work-remove-${entry.id}`}
                  aria-label="Remove file"
                  className="w-6 h-6 border border-[#333] text-[#666] hover:text-white hover:border-[#90E0EF]/40 flex items-center justify-center transition-colors"
                  style={{ cursor: 'pointer' }}
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>

          {/* Optional fields */}
          <div className="mt-3 grid grid-cols-1 gap-2.5">
            <input
              type="text"
              value={entry.title || ''}
              onChange={(e) => onChange(entry.id, { title: e.target.value })}
              placeholder="Optional title"
              data-testid={`work-title-${entry.id}`}
              className="w-full bg-transparent border border-[#222] text-white font-mono text-xs h-9 px-2.5 placeholder:text-[#555] focus:outline-none focus:border-[#90E0EF]"
              disabled={entry.status === 'uploading'}
            />
            <textarea
              value={entry.description || ''}
              onChange={(e) => onChange(entry.id, { description: e.target.value })}
              placeholder="Optional description"
              rows={2}
              data-testid={`work-description-${entry.id}`}
              className="w-full bg-transparent border border-[#222] text-white font-mono text-xs px-2.5 py-2 placeholder:text-[#555] focus:outline-none focus:border-[#90E0EF] resize-none"
              disabled={entry.status === 'uploading'}
            />
          </div>

          {/* Progress bar */}
          {entry.status === 'uploading' && (
            <div className="mt-2 w-full h-px bg-[#222] overflow-hidden">
              <motion.div
                className="h-full bg-[#90E0EF]"
                animate={{ width: `${entry.progress || 0}%` }}
                transition={{ duration: 0.2 }}
              />
            </div>
          )}

          {entry.error && (
            <p className="mt-2 text-[10px] font-mono text-red-400">{entry.error}</p>
          )}
        </div>
      </div>
    </motion.div>
  );
}
